import { assertNever } from '@kofno/piper';
import { just, Maybe, nothing } from 'maybeasy';
import { action, computed, makeObservable, observable } from 'mobx';
import { Avatar, avatarFrom } from '../Avatar';
import { StringOption } from '../components/Options/Types';
import { findLink } from '../LinkyLinky';
import { Link } from '../Resource/Types';
import { TPlainTextKey } from '../Translations';
import {
  anonymous,
  Authenticated,
  errored,
  identified,
  identifying,
  loggingOut,
  State,
  unauthorized,
  UserResource,
  waiting,
} from './Types';

class CurrentUserStore {
  @observable
  currentUserState: State = waiting();

  constructor() {
    makeObservable(this);
  }

  @action
  setCurrentUser = (currentUserResource: UserResource) => {
    switch (this.currentUserState.kind) {
      case 'identified':
      case 'identifying':
      case 'logging-out':
        this.currentUserState = identified(currentUserResource);
        break;
      case 'waiting':
      case 'anonymous':
      case 'unauthorized':
      case 'errored':
        break;
      default:
        assertNever(this.currentUserState);
    }
  };

  @action
  error = (msg: TPlainTextKey) => {
    this.currentUserState = anonymous(just(msg));
  };

  @action
  setIdentifying = () => {
    this.currentUserState = identifying();
  };

  @action
  setAnonymous = (failureMessage: Maybe<TPlainTextKey>) => {
    this.currentUserState = anonymous(failureMessage);
  };

  @action
  setUnauthorized = (failureMessage: TPlainTextKey): void => {
    this.currentUserState = unauthorized(failureMessage);
  };

  @action
  setErrored = (failureMessage: TPlainTextKey): void => {
    this.currentUserState = errored(failureMessage);
  };

  @action
  logout = (): void => {
    switch (this.currentUserState.kind) {
      case 'identified':
        this.currentUserState = loggingOut(this.currentUserState.currentUserResource);
        break;
      case 'waiting':
      case 'identifying':
      case 'anonymous':
      case 'unauthorized':
      case 'logging-out':
      case 'errored':
        break;
      default:
        assertNever(this.currentUserState);
    }
  };

  @computed
  get identified(): Maybe<Authenticated> {
    switch (this.currentUserState.kind) {
      case 'identified':
        return just(this.currentUserState);
      case 'waiting':
      case 'anonymous':
      case 'unauthorized':
      case 'identifying':
      case 'logging-out':
      case 'errored':
        return nothing();
    }
  }

  @computed
  get email(): Maybe<string> {
    switch (this.currentUserState.kind) {
      case 'identified':
      case 'logging-out':
        return just(this.currentUserState.currentUserResource.payload.email);
      case 'anonymous':
        return this.currentUserState.email;
      case 'unauthorized':
      case 'waiting':
      case 'identifying':
      case 'errored':
        return nothing();
    }
  }

  @computed
  get initials(): Maybe<string> {
    switch (this.currentUserState.kind) {
      case 'identified':
      case 'logging-out':
        return just(this.currentUserState.currentUserResource.payload.initials);
      case 'anonymous':
      case 'unauthorized':
      case 'waiting':
      case 'identifying':
      case 'errored':
        return nothing();
    }
  }

  @computed
  get avatar(): Avatar {
    return avatarFrom(this.photo, this.initials);
  }

  @computed
  get photo(): Maybe<Link> {
    switch (this.currentUserState.kind) {
      case 'identified':
      case 'logging-out':
        return findLink('avatar', this.links);
      case 'anonymous':
      case 'unauthorized':
      case 'waiting':
      case 'identifying':
      case 'errored':
        return nothing();
    }
  }

  @computed
  get organizationOption(): Maybe<StringOption> {
    switch (this.currentUserState.kind) {
      case 'identified':
      case 'logging-out':
        const { organizationId, organizationName } =
          this.currentUserState.currentUserResource.payload;
        return just({ value: String(organizationId), label: organizationName });
      case 'anonymous':
      case 'unauthorized':
      case 'waiting':
      case 'identifying':
      case 'errored':
        return nothing();
    }
  }

  get exoOrg(): boolean {
    switch (this.currentUserState.kind) {
      case 'identified':
      case 'logging-out':
        return this.currentUserState.currentUserResource.payload.exoOrg;
      case 'anonymous':
      case 'unauthorized':
      case 'waiting':
      case 'identifying':
      case 'errored':
        return false;
    }
  }

  @computed
  get name(): Maybe<string> {
    switch (this.currentUserState.kind) {
      case 'identified':
      case 'logging-out':
        return this.currentUserState.currentUserResource.payload.name;
      case 'anonymous':
      case 'unauthorized':
      case 'waiting':
      case 'identifying':
      case 'errored':
        return nothing();
    }
  }

  @computed
  get exoChatbotAccessible(): boolean {
    switch (this.currentUserState.kind) {
      case 'identified':
      case 'logging-out':
        return this.currentUserState.currentUserResource.payload.exoChatbotAccessible;
      case 'anonymous':
      case 'unauthorized':
      case 'waiting':
      case 'identifying':
      case 'errored':
        return false;
    }
  }

  @computed
  get links(): ReadonlyArray<Link> {
    switch (this.currentUserState.kind) {
      case 'identified':
      case 'logging-out':
        return this.currentUserState.currentUserResource.links;
      case 'waiting':
      case 'anonymous':
      case 'unauthorized':
      case 'identifying':
      case 'errored':
        return [];
    }
  }
}

const currentUserStore = new CurrentUserStore();

export { currentUserStore, type CurrentUserStore };
